import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  Skeleton,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Toll as CreditIcon,
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useCredits } from '../hooks';
import { useDocumentTitle } from '../hooks/core';
import { productsApi, type PaymentProduct, type PaymentPrice } from '../api';
import { CheckoutModal } from '../components/checkout';
import { trackViewItemList, trackViewItem, trackAddToCart, type CreditPack } from '../lib/analytics';

export const CreditsPage: React.FC = () => {
  const { credits, isLoading: loading, error: creditsError } = useCredits();
  const error = creditsError ? 'Failed to load credits' : null;
  const [searchParams, setSearchParams] = useSearchParams();

  useDocumentTitle('Credits');

  const [products, setProducts] = useState<PaymentProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);

  // Checkout modal state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PaymentProduct | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<PaymentPrice | null>(null);

  const hasCredits = credits && credits.availableCredits > 0;

  // Track if we've fired view_item_list for this page load
  const hasTrackedViewItemList = useRef(false);
  // Track if we've handled the auto-open buy param
  const hasHandledBuyParam = useRef(false);

  // Helper to get the primary price for a product (defined early so other helpers can use it)
  const getPrimaryPrice = useCallback((product: PaymentProduct): PaymentPrice | undefined => {
    return product.prices?.find(p => p.active) || product.prices?.[0];
  }, []);

  // Helper to convert PaymentProduct to CreditPack for analytics
  const toCreditPack = useCallback((product: PaymentProduct): CreditPack => {
    const primaryPrice = getPrimaryPrice(product);
    return {
      id: product.id,
      lookupKey: product.lookupKey,
      name: product.name,
      description: product.description,
      credits: product.metadata.boardCredits || 0,
      price: primaryPrice?.amount || 0,
      currency: primaryPrice?.currency || 'usd',
    };
  }, [getPrimaryPrice]);

  // Fetch credit packs
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const creditPacks = await productsApi.listCreditPacks();
        setProducts(creditPacks);
        setProductsError(null);

        // Track view_item_list event when products are loaded (only once)
        if (creditPacks.length > 0 && !hasTrackedViewItemList.current) {
          const packs: CreditPack[] = creditPacks.map((p) => {
            const price = p.prices?.find((pr) => pr.active) || p.prices?.[0];
            return {
              id: p.id,
              lookupKey: p.lookupKey,
              name: p.name,
              description: p.description,
              credits: p.metadata.boardCredits || 0,
              price: price?.amount || 0,
              currency: price?.currency || 'usd',
            };
          });
          trackViewItemList(packs);
          hasTrackedViewItemList.current = true;
        }
      } catch (_err) {
        setProductsError('Failed to load credit packs');
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Auto-open checkout modal if ?buy=true is in URL
  useEffect(() => {
    const shouldOpenCheckout = searchParams.get('buy') === 'true';

    if (shouldOpenCheckout && products.length > 0 && !hasHandledBuyParam.current) {
      hasHandledBuyParam.current = true;
      // Select the "popular" (middle) product, or first if only one
      const popularIndex = products.length > 1 ? 1 : 0;
      const product = products[popularIndex];
      const price = getPrimaryPrice(product);
      if (product && price) {
        const creditPack = toCreditPack(product);
        trackViewItem(creditPack);
        trackAddToCart(creditPack);
        setSelectedProduct(product);
        setSelectedPrice(price);
        setCheckoutOpen(true);
        // Remove the buy param from URL without navigation
        searchParams.delete('buy');
        setSearchParams(searchParams, { replace: true });
      }
    }

    // Reset the flag when buy param is not present (so it works on subsequent visits)
    if (!shouldOpenCheckout) {
      hasHandledBuyParam.current = false;
    }
  }, [products, searchParams, setSearchParams, toCreditPack, getPrimaryPrice]);

  const handlePurchase = (product: PaymentProduct, price: PaymentPrice) => {
    const creditPack = toCreditPack(product);

    // Track view_item when user clicks on a product (viewing details before purchase)
    trackViewItem(creditPack);

    // Track add_to_cart when user decides to purchase
    trackAddToCart(creditPack);

    setSelectedProduct(product);
    setSelectedPrice(price);
    setCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setCheckoutOpen(false);
    setSelectedProduct(null);
    setSelectedPrice(null);
  };

  const handleCheckoutSuccess = () => {
    // Credits context will refresh automatically
  };

  // Helper to format price
  const formatPrice = (price: PaymentPrice): string => {
    return `$${(price.amount / 100).toFixed(2)}`;
  };

  // Helper to calculate savings percentage (compared to first product)
  const getSavingsPercent = (product: PaymentProduct, index: number): number | null => {
    if (index === 0 || products.length < 2) return null;

    const baseProduct = products[0];
    const basePrice = getPrimaryPrice(baseProduct);
    const currentPrice = getPrimaryPrice(product);

    if (!basePrice || !currentPrice) return null;

    const baseCredits = baseProduct.metadata.boardCredits || 1;
    const currentCredits = product.metadata.boardCredits || 1;

    const basePricePerCredit = basePrice.amount / baseCredits;
    const currentPricePerCredit = currentPrice.amount / currentCredits;

    const savings = ((basePricePerCredit - currentPricePerCredit) / basePricePerCredit) * 100;
    return savings > 0 ? Math.round(savings) : null;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h4" component="h1">
          Board Credits
        </Typography>
        <Tooltip title="Learn more about credits">
          <IconButton
            component={RouterLink}
            to="/support/credits"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Credits are used to create new boards. Each board creation uses 1 credit.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Current Balance */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CreditIcon color={hasCredits ? 'primary' : 'warning'} sx={{ fontSize: 32 }} />
          <Typography variant="h5">Your Balance</Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Skeleton variant="rectangular" width={100} height={80} />
            <Skeleton variant="rectangular" width={100} height={80} />
            <Skeleton variant="rectangular" width={100} height={80} />
          </Box>
        ) : credits ? (
          <>
            <Grid container spacing={4}>
              <Grid size="grow">
                <Typography variant="h2" color={hasCredits ? 'primary.main' : 'warning.main'}>
                  {credits.availableCredits}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available
                </Typography>
              </Grid>
              <Grid size="grow">
                <Typography variant="h2" color="text.secondary">
                  {credits.usedCredits}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Used
                </Typography>
              </Grid>
              <Grid size="grow">
                <Typography variant="h2" color="text.secondary">
                  {credits.totalCredits}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
              </Grid>
            </Grid>

            {!hasCredits && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                You don't have any credits available. Purchase more to create boards.
              </Alert>
            )}
          </>
        ) : null}
      </Paper>

      {/* Purchase Credits */}
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <ShoppingCartIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5">Get More Credits</Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Need more credits? Purchase additional credits to create more boards.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Credit Packages */}
        {productsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : productsError ? (
          <Alert severity="error">{productsError}</Alert>
        ) : products.length === 0 ? (
          <Alert severity="info">
            No credit packs are currently available. Please check back later or contact support.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {products.map((product, index) => {
              const price = getPrimaryPrice(product);
              const credits = product.metadata.boardCredits || 0;
              const savings = getSavingsPercent(product, index);
              // Best value = lowest cost per board
              const isBestValue = products.length > 1 && products.every(p => {
                const pPrice = getPrimaryPrice(p);
                const pCredits = p.metadata.boardCredits || 1;
                const pCostPerBoard = (pPrice?.amount || 0) / pCredits;
                const thisCostPerBoard = (price?.amount || 0) / (credits || 1);
                return thisCostPerBoard <= pCostPerBoard;
              });

              if (!price) return null;

              return (
                <Grid size={{ xs: 12, sm: 4 }} key={product.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      border: 2,
                      borderColor: isBestValue ? 'primary.main' : 'divider',
                      position: 'relative',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    {isBestValue && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: 'primary.main',
                          color: 'white',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                      >
                        BEST VALUE
                      </Box>
                    )}
                    <Typography variant="h4" color="primary">{credits}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Credit{credits !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="h5" sx={{ mb: savings ? 1 : 2 }}>
                      {formatPrice(price)}
                    </Typography>
                    {savings && (
                      <Typography variant="caption" color="success.main" display="block" sx={{ mb: 2 }}>
                        Save {savings}%
                      </Typography>
                    )}
                    <Button
                      variant={isBestValue ? 'contained' : 'outlined'}
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={() => handlePurchase(product, price)}
                    >
                      Purchase
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}

        {products.length > 0 && (
          <Alert severity="info" sx={{ mt: 4 }} icon={false}>
            <Typography variant="body2">
              Payments are processed securely through Stripe. Your payment information is never stored on our servers.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            component={RouterLink}
            to="/profile/transactions"
            variant="text"
            color="primary"
          >
            View Transaction History
          </Button>
        </Box>
      </Paper>

      {/* Checkout Modal */}
      {selectedProduct && selectedPrice && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={handleCheckoutClose}
          onSuccess={handleCheckoutSuccess}
          product={selectedProduct}
          price={selectedPrice}
        />
      )}
    </Box>
  );
};
